import { useEffect } from 'react';
import {
    ApplicationInsights,
    DistributedTracingModes,
    ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import getRuntimeConfig from '~/services/runtime-config.service';
import storageHelpers from '~/utils/storage-helpers';

const useAppInsights = () => {
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const setupAppInsights = async () => {
            const reactPlugin = new ReactPlugin();

            const runtimeConfig = await getRuntimeConfig();
            const connectionString = runtimeConfig?.appInsightsConnectionString;
            const roleName = runtimeConfig?.appInsightsRoleName;

            if (!connectionString || !roleName) {
                console.warn(
                    'Application Insights is not configured. Connection string or role name is missing.'
                );
                return;
            }

            const appInsights = new ApplicationInsights({
                config: {
                    connectionString,
                    enableAutoRouteTracking: true,
                    enableCorsCorrelation: true,
                    distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
                    // Chrome blocks 'unload'; SDK still flushes on pagehide/visibilitychange.
                    disablePageUnloadEvents: ['unload'],
                    extensions: [reactPlugin],
                },
            });

            appInsights.loadAppInsights();

            const getAppInsightsSessionId = () =>
                appInsights.context?.sessionManager?.automaticSession?.id ?? '';

            const setSessionCookie = (sessionId: string) => {
                const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
                document.cookie = `x-session-id=${encodeURIComponent(sessionId)}; path=/; SameSite=Lax${secureFlag}`;
            };

            const clearSessionCookie = () => {
                const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
                document.cookie = `x-session-id=; path=/; Max-Age=0; SameSite=Lax${secureFlag}`;
            };

            const initialSessionId = getAppInsightsSessionId();
            if (initialSessionId) {
                storageHelpers.setSessionStorageItem('x-session-id', initialSessionId, true);
                setSessionCookie(initialSessionId);
            } else {
                storageHelpers.removeSessionStorageItem('x-session-id');
                clearSessionCookie();
            }

            appInsights.addTelemetryInitializer((env: ITelemetryItem) => {
                const sessionId = getAppInsightsSessionId();
                if (sessionId) {
                    storageHelpers.setSessionStorageItem('x-session-id', sessionId, true);
                    setSessionCookie(sessionId);
                } else {
                    storageHelpers.removeSessionStorageItem('x-session-id');
                    clearSessionCookie();
                }

                env.tags = env.tags || {};
                env.tags['ai.cloud.role'] = roleName;
            });
        };

        setupAppInsights();
    }, []);

    return null;
};

export { useAppInsights };
